import { Field, FieldProps } from 'formik';
import { ChangeEvent, FocusEvent, useState } from 'react';
import { formatGroupedNumber, formatRawNumber, parseGroupedNumber } from 'shared/utils/groupedNumber';
import { InputWithUnit, InputWithUnitProps } from '../fragments/InputWithUnit';

interface FormikInputWithUnitProps extends InputWithUnitProps {
  name: string;
  disabled?: boolean;
  type?: string;
  pattern?: string
  className?: string
  /**
   * Shows the number with its thousands grouped ("300 000") once the field is left, and the raw
   * digits while it is being typed; the form keeps the raw number. The input is then a text one:
   * a native number input cannot show separators.
   */
  groupThousands?: boolean
}

interface GroupedInputProps extends Omit<FormikInputWithUnitProps, 'name' | 'groupThousands'> {
  fieldProps: FieldProps
}

/**
 * Text input bound to a numeric form value: the raw digits while the field has the focus (nothing
 * moves under the fingers), the grouped number once it is left.
 */
const GroupedInputWithUnit: React.FC<GroupedInputProps> = ({ fieldProps, onBlur, type: _type, min: _min, max: _max, step: _step, ...props }) => {
  const { field, meta, form } = fieldProps;
  const [isFocused, setIsFocused] = useState(false);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const raw = parseGroupedNumber(event.target.value);
    // An integer is stored as a number, like a native number input did. While decimals are being
    // typed ("12." then "12.0") the raw text is kept, or the separator and trailing zeros would vanish.
    form.setFieldValue(field.name, raw === '' || raw.includes('.') ? raw : Number(raw));
  };

  const handleBlur = (event: FocusEvent<HTMLInputElement>) => {
    setIsFocused(false);
    (onBlur ?? field.onBlur)(event);
  };

  return (
    <InputWithUnit
      {...props}
      name={field.name}
      type="text"
      inputMode="decimal"
      value={isFocused ? formatRawNumber(field.value) : formatGroupedNumber(field.value)}
      onChange={handleChange}
      onFocus={() => setIsFocused(true)}
      onBlur={handleBlur}
      error={meta.touched && meta.error ? meta.error : ''}
    />
  );
};

export const FormikInputWithUnit: React.FC<FormikInputWithUnitProps> = ({
  name,
  onChange,
  onBlur,
  groupThousands,
  ...props
}) => {
  return (
    <Field name={name}>
      {(fieldProps: FieldProps) => groupThousands
        ? <GroupedInputWithUnit {...props} onBlur={onBlur} fieldProps={fieldProps} />
        : (
          <InputWithUnit
            {...props}
            {...fieldProps.field}
            {...(onChange ? { onChange } : {})}
            {...(onBlur ? { onBlur } : {})}
            error={fieldProps.meta.touched && fieldProps.meta.error ? fieldProps.meta.error : ''}
          />
        )}
    </Field>
  );
};
